import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useCompactPortrait, useNavigationAction } from 'components/navigation/NavigationContext';

export default function StandardModal({ show, handleClose, title, headerMeta = null, headerActions = null, footerMeta = null, ModalBodyComponent, ModalFooterComponent = null, style = {}, bodyClassName = '', fullscreen = false, hideLogo = false, hideCollapse = false, navigationParent = null }) {
  const compact = useCompactPortrait();
  const embedded = compact && Boolean(navigationParent);
  const [embeddedHost, setEmbeddedHost] = useState(null);
  useNavigationAction(`modal-${navigationParent}`, embedded && show ? {
    label: title,
    parent: navigationParent,
    onClick: handleClose,
    renderContent: () => <div ref={setEmbeddedHost} className="mobile-tool-detail" />,
  } : null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dragStateRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const didMoveRef = useRef(false);
  const suppressHideRef = useRef(false);
  const rafRef = useRef(null);
  const modalIdRef = useRef(`standard-modal-${Math.random().toString(36).slice(2, 10)}`);

  const applyDialogStyle = useCallback(() => {
    const dialog = document.querySelector(`.${modalIdRef.current}`);
    if (!dialog) return;

    dialog.style.setProperty('--standard-modal-top', style.marginTop || '10vh');
    dialog.style.setProperty('--standard-modal-max-width', style.maxWidth || '1200px');
    dialog.style.setProperty('--standard-modal-offset-x', `${offsetRef.current.x}px`);
    dialog.style.setProperty('--standard-modal-offset-y', `${offsetRef.current.y}px`);
  }, [style]);

  const scheduleApplyDialogStyle = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      applyDialogStyle();
    });
  }, [applyDialogStyle]);

  useEffect(() => {
    if (!show) {
      offsetRef.current = { x: 0, y: 0 };
      dragStateRef.current = null;
      isDraggingRef.current = false;
      didMoveRef.current = false;
      suppressHideRef.current = false;
      setIsCollapsed(false);
    }
  }, [show]);

  const startDrag = useCallback((event) => {
    if (fullscreen) return;
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    const point = event.touches?.[0] || event;
    dragStateRef.current = {
      startX: point.clientX,
      startY: point.clientY,
      originX: offsetRef.current.x,
      originY: offsetRef.current.y,
    };
    isDraggingRef.current = true;
    didMoveRef.current = false;
    if (event.cancelable) {
      event.preventDefault();
    }
  }, [fullscreen]);

  useEffect(() => {
    if (!show) return;
    applyDialogStyle();
  }, [show, applyDialogStyle]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
        didMoveRef.current = true;
      }
      offsetRef.current = { x: dragState.originX + deltaX, y: dragState.originY + deltaY };
      scheduleApplyDialogStyle();
    };

    const handlePointerUp = () => {
      if (isDraggingRef.current && didMoveRef.current) {
        suppressHideRef.current = true;
        window.setTimeout(() => {
          suppressHideRef.current = false;
        }, 180);
      }
      isDraggingRef.current = false;
      dragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scheduleApplyDialogStyle]);

  const guardedHandleClose = useCallback(() => {
    if (isDraggingRef.current || suppressHideRef.current) return;
    handleClose();
  }, [handleClose]);

  if (embedded) return show && embeddedHost ? createPortal(
    <>
      <div className="mobile-tool-header-actions d-flex flex-wrap align-items-center justify-content-end gap-2">{headerMeta}{headerActions}</div>
      <div className={`mobile-tool-body ${bodyClassName}`}>{ModalBodyComponent}</div>
      {(ModalFooterComponent || footerMeta) && (
        <div className="mobile-tool-footer d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
          {footerMeta}{ModalFooterComponent}
        </div>
      )}
    </>,
    embeddedHost
  ) : null;

  return (
    <Modal
      show={show}
      enforceFocus={!compact}
      onHide={guardedHandleClose}
      size="xl"
      fullscreen={fullscreen}
      className={bodyClassName ? `${bodyClassName}-shell` : undefined}
      dialogClassName={`standard-modal-dialog ${modalIdRef.current}`}
    >
      <Modal.Header className="standard-modal-header">
        <div
          onPointerDown={fullscreen ? undefined : startDrag}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', cursor: fullscreen ? 'default' : 'move', touchAction: fullscreen ? 'auto' : 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: '1rem' }}>
            {!hideLogo && (
              <img
                src="/TMLogo.png"
                alt="Toastmasters Logo"
                className="modal-header-logo"
              />
            )}
            <Modal.Title className="modal-header-title">
              {title || 'STANDARD'}
            </Modal.Title>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {headerMeta ? (
              <div className="standard-modal-header-meta" onPointerDown={(event) => event.stopPropagation()}>
                {headerMeta}
              </div>
            ) : null}
            <div className="standard-modal-header-actions">
            {headerActions ? (
              <div onPointerDown={(event) => event.stopPropagation()}>
                {headerActions}
              </div>
            ) : null}
            {!hideCollapse && (
              <Button
                type="button"
                variant="link"
                className="standard-modal-collapse-btn"
                onClick={() => setIsCollapsed((value) => !value)}
                aria-label={isCollapsed ? 'Expand modal' : 'Collapse modal'}
              >
                <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-dash-lg'}`} aria-hidden="true" />
              </Button>
            )}
            <Button
              type="button"
              variant="link"
              className="standard-modal-close-btn"
              onClick={guardedHandleClose}
              aria-label="Close modal"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </Button>
            </div>
          </div>
        </div>
      </Modal.Header>
      {!isCollapsed && (
        <>
          <Modal.Body className={bodyClassName}>
            {ModalBodyComponent ? ModalBodyComponent : null}
          </Modal.Body>
          {ModalFooterComponent || footerMeta ? (
            <Modal.Footer
              className="standard-modal-footer"
              style={fullscreen ? { flexShrink: 0, paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' } : undefined}
            >
              <div className="w-100 d-flex align-items-center justify-content-between" onPointerDown={(event) => event.stopPropagation()}>
                <div className="standard-modal-footer-meta">
                  {footerMeta}
                </div>
                <div>
                  {ModalFooterComponent}
                </div>
              </div>
            </Modal.Footer>
          ) : null}
        </>
      )}
    </Modal>
  );
}
